import { KmatType } from "~/features/configurator/Linear/kmatType";
import type {
  Kmat,
  LinearSubstep,
  MainKmatSubstep,
  SubKmatSubstep,
  SubstepCstics,
} from "~/features/configurator/Linear/models";
import { isNonNull } from "~/common/utils/reducers";

export function pushStandardValueIfMissing(kmat: Kmat, csticName: string, value: string) {
  const cstic = kmat.cstics[csticName];
  if (cstic) {
    const standardValue = cstic.values.find((item) => item.key == value);
    if (!standardValue) {
      cstic.values.push({
        key: value,
        value: value,
        standard: true,
        label: "",
        info: "",
        imageUrl: "",
      });
    }
  }
}

export function getCsticsValue(mainKmat: Kmat, ...cstics: string[]) {
  return cstics.map((cstic) => mainKmat.cstics[cstic]?.value ?? "");
}

export function getCsticNumericValue(mainKmat: Kmat, cstic: string, defaultValue = 0): number {
  return Number(mainKmat.cstics[cstic]?.value ?? defaultValue);
}

export function getCsticPossibleValues(mainKmat: Kmat, cstic: string) {
  return (mainKmat.cstics[cstic]?.values ?? []).map((v) => v.value);
}

export function csticHasValue(mainKmat: Kmat, cstic: string) {
  const val = mainKmat.cstics[cstic]?.value;
  return val !== null && val !== undefined && val !== "";
}

export function csticsHaveValue(mainKmat: Kmat, ...cstics: string[]) {
  return cstics.every((cstic) => csticHasValue(mainKmat, cstic));
}

export function join(separator: string, ...values: string[]) {
  return values.filter(isNonNull).join(separator);
}

export function isMainKmatSubstep(substep: Pick<LinearSubstep, "kmatType">): substep is MainKmatSubstep {
  return substep.kmatType === KmatType.Main;
}

export function isSubKmatSubstep(substep: Pick<LinearSubstep, "kmatType">): substep is SubKmatSubstep {
  return !isMainKmatSubstep(substep);
}

export function getKmatCstics(substep: Pick<LinearSubstep, "kmatType">, index: number): SubstepCstics {
  if (isMainKmatSubstep(substep)) {
    return substep.cstics;
  } else if (isSubKmatSubstep(substep)) {
    return substep.cstics[index];
  }
  throw new Error("Not Supported");
}

export function getMainKmatCstics(substep: LinearSubstep) {
  if (!isMainKmatSubstep(substep)) {
    throw new Error(`Wrong kmat type for ${substep.type}`);
  }
  return substep.cstics;
}

export function getSubKmatCstics(substep: LinearSubstep, index: number) {
  if (!isSubKmatSubstep(substep)) {
    throw new Error(`Wrong kmat type for ${substep.type}`);
  }
  return substep.cstics[index];
}
